import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';

import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private readonly apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  /**
   * Método para realizar requisições GET ao endpoint
   * @param endpoint
   * @param params
   * @returns
   */
  get<TResponse>(endpoint: string, params?: Record<string, any>): Observable<TResponse> {
    let httpParams = new HttpParams();

    if (params) {
      for (const key in params) {
        if (Object.prototype.hasOwnProperty.call(params, key)) {
          const value = params[key];
          if (value !== null && value !== undefined && value !== '') {
            if (Array.isArray(value)) {
              value.forEach((item: any) => {
                httpParams = httpParams.append(key, item.toString());
              });
            } else {
              httpParams = httpParams.set(key, value.toString());
            }
          }
        }
      }
    }
    return this.http.get<TResponse>(`${this.apiUrl}/${endpoint}`, { params: httpParams }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  /**
   * Método para realizar requisições POST ao endpoint
   * @param endpoint
   * @param payload
   * @returns
   */
  post<TResponse, TPayload>(endpoint: string, payload?: TPayload): Observable<TResponse> {
    return this.http.post<TResponse>(`${this.apiUrl}/${endpoint}`, payload).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Método para realizar requisições PUT ao endpoint
   * @param endpoint
   * @param id
   * @param payload
   * @returns
   */
  put<TResponse, TPayload>(endpoint: string, id?: number | string, payload?: TPayload): Observable<TResponse> {
    return this.http.put<TResponse>(`${this.apiUrl}/${endpoint}/${id}`, payload).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Método para realizar requisições PATCH ao endpoint
   * @param endpoint
   * @param id
   * @param complement
   * @param payload
   * @returns
   */
  patch<TResponse, TPayload>(endpoint: string, id?: number | string, complement?: string, payload?: TPayload): Observable<TResponse> {
    return this.http.patch<TResponse>(`${this.apiUrl}/${endpoint}/${id}${complement}`, payload).pipe(
      catchError(this.handleError)
    );
  }

   /**
   * Método para realizar requisições PATCH ao endpoint
   * @param endpoint
   * @param id
   * @returns
   */
  delete<TResponse>(endpoint: string, id?: number | string): Observable<TResponse> {
    return this.http.delete<TResponse>(`${this.apiUrl}/${endpoint}/${id}`).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Método genérico para tratamento de erros HTTP
   * @param error
   * @returns
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage: string;

    if(error.error.message){
      return throwError(() => error);
    }

    if (error.error instanceof ErrorEvent) {
      errorMessage = `Ocorreu um erro no cliente: ${error.error.message}`;
    } else {
      switch (error.status) {

        case 0:
          errorMessage = 'Não foi possível conectar ao servidor. Verifique sua conexão.';
          break;
        case 400:
          errorMessage = 'Requisição inválida. Verifique os dados enviados.';
          break;
        case 401:
          errorMessage = 'Não autorizado. Verifique suas credenciais.';
          break;
        case 403:
          errorMessage = 'Acesso proibido. Você não tem permissão para acessar este recurso.';
          break;
        case 404:
          errorMessage = 'Recurso não encontrado.';
          break;
        case 500:
          errorMessage = 'Erro interno no servidor. Tente novamente mais tarde.';
          break;
        default:
          errorMessage = `Erro inesperado: ${error.message}`;
      }
    }

    return throwError(() => new Error(errorMessage));
  }
}
